import React from "react";
import {
  Grid, Typography, TextField, IconButton, Tooltip,
  InputAdornment, Autocomplete, Divider, Button
} from "@mui/material";
import { Visibility, VisibilityOff, AddCircle as AddCircleIcon, Delete as DeleteIcon, CloudUpload as CloudUploadIcon, Download as DownloadIcon, Phone as PhoneIcon } from "@mui/icons-material";

const BankDetailsForm = ({
  index,
  bankDetail,
  bankAccounts,
  showPassword,
  accountMismatch,
  handleInputChange,
  handleAutocompleteChange,
  handleClickShowPassword,
  handleMouseDownPassword,
  handleBankLetterInputChange,
  handleDownloadBankLetter,
  removeBankDetail,
  handleChequeInputChange,
  handleRTGSInputChange,
  setAccountMismatch,
  handleOpenModal
}) => {
  return (
    <Grid container spacing={2}>
      {/* Left Side - Bank Info */}
      <Grid item xs={12} md={6}>
        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
          Bank {index + 1}
        </Typography>

        {/* Account Holder Name */}
        <FormRow
          label="Account Holder Name"
          input={
            <TextField
              fullWidth size="small"
              name="accountHolderName"
              value={bankDetail.accountHolderName}
              onChange={(e) => handleInputChange(index, e)}
            />
          }
        />

        {/* IFSC */}
        <FormRow
          label="IFSC"
          input={
            <TextField
              fullWidth size="small"
              name="ifsc"
              value={bankDetail.ifsc}
              onChange={(e) => handleInputChange(index, e)}
            />
          }
        />

        {/* Bank Name */}
        <FormRow
          label="Bank Name"
          input={
            <TextField
              fullWidth size="small"
              name="bankName"
              value={bankDetail.bankName}
              onChange={(e) => handleInputChange(index, e)}
            />
          }
        />

        {/* Account Number */}
        <FormRow
          label="Account Number"
          input={
            <TextField
              fullWidth size="small"
              name="accountNumber"
              value={bankDetail.accountNumber}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d{0,18}$/.test(value)) {
                  handleInputChange(index, e);
                }
              }}
              error={!/^\d{9,18}$/.test(bankDetail.accountNumber) && bankDetail.accountNumber.length > 0}
              helperText={
                bankDetail.accountNumber.length > 0 &&
                !/^\d{9,18}$/.test(bankDetail.accountNumber)
                  ? "Enter a valid account number (9–18 digits)"
                  : ""
              }
              type={showPassword ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">
                    <IconButton
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          }
        />

        {/* Re-enter Account Number */}
        <FormRow
          label="Re-enter Account Number"
          input={
            <TextField
              fullWidth size="small"
              name="reEnterAccountNumber"
              value={bankDetail.reEnterAccountNumber}
              onChange={(e) => handleInputChange(index, e)}
              onBlur={() => {
                setAccountMismatch(bankDetail.accountNumber?.trim() !== bankDetail.reEnterAccountNumber?.trim());
              }}
              error={accountMismatch}
              helperText={accountMismatch ? "Account numbers do not match" : ""}
            />
          }
          rightAction={
            index > 0 && (
              <IconButton onClick={() => removeBankDetail(index)} color="secondary">
                <DeleteIcon />
              </IconButton>
            )
          }
        />

        {/* Bank Letter Upload */}
        <FileUploadRow
          label="Bank Letter"
          file={bankDetail.bankLetter}
          fileName={bankDetail.bankLetter_file_name}
          onUpload={(e) => handleBankLetterInputChange(index, e)}
          onDownload={(e) => handleDownloadBankLetter(index, e, "bank_letter")}
        />
      </Grid>

      {/* Right Side - Branch Info */}
      <Grid item xs={12} md={6}>
        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
          Branch Details
        </Typography>

        {/* Account Type */}
        <FormRow
          label="Account Type"
          input={
            <Autocomplete
              fullWidth size="small"
              options={bankAccounts}
              getOptionLabel={(option) => option.label}
              value={bankAccounts.find((opt) => opt.id === bankDetail.accountTypeId) || null}
              onChange={(e, newValue) =>
                handleAutocompleteChange(index, "accountTypeId", newValue ? newValue.id : null)
              }
              renderInput={(params) => <TextField {...params} />}
            />
          }
          rightAction={
            <Tooltip title="Add New Category">
              <IconButton size="small" color="secondary" onClick={handleOpenModal}>
                <AddCircleIcon />
              </IconButton>
            </Tooltip>
          }
        />

        {/* Branch */}
        <FormRow
          label="Branch"
          input={
            <TextField
              fullWidth size="small"
              name="branchName"
              value={bankDetail.branchName}
              onChange={(e) => handleInputChange(index, e)}
            />
          }
        />

        {/* Address */}
        <FormRow
          label="Address"
          input={
            <TextField
              fullWidth size="small"
              name="bankAddress"
              value={bankDetail.bankAddress}
              onChange={(e) => handleInputChange(index, e)}
            />
          }
        />

        {/* Contact Number */}
        <FormRow
          label="Contact Number"
          input={
            <TextField
              fullWidth size="small"
              name="contactNumber"
              value={bankDetail.contactNumber}
              onChange={(e) => {
                const val = e.target.value;
                if (/^\d*$/.test(val) && val.length <= 10) handleInputChange(index, e);
              }}
              error={bankDetail.contactNumber && !/^[0-9]{10}$/.test(bankDetail.contactNumber)}
              helperText={
                bankDetail.contactNumber && !/^[0-9]{10}$/.test(bankDetail.contactNumber)
                  ? "Enter a valid 10-digit contact number"
                  : ""
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon />
                  </InputAdornment>
                )
              }}
            />
          }
        />

        {/* Cancelled Cheque */}
        <FileUploadRow
          label="Upload Cancelled Cheque"
          file={bankDetail.cancelledCheque}
          fileName={bankDetail.cancelledCheque_file_name}
          onUpload={(e) => handleChequeInputChange(index, e)}
          onDownload={(e) => handleDownloadBankLetter(index, e, "cancel_cheque")}
        />

        {/* RTGS Details */}
        <FileUploadRow
          label="Upload RTGS Details"
          file={bankDetail.rtgsDetail}
          fileName={bankDetail.rtgsDetail_file_name}
          onUpload={(e) => handleRTGSInputChange(index, e)}
          onDownload={(e) => handleDownloadBankLetter(index, e, "rtgs")}
        />
      </Grid>

      <Grid item xs={12}>
        <Divider />
      </Grid>
    </Grid>
  );
};

// Subcomponent for normal row
const FormRow = ({ label, input, rightAction }) => (
  <Grid container spacing={2} alignItems="center" sx={{ py: 1 }}>
    <Grid item xs={12} md={3}>
      <Typography variant="body2">{label}</Typography>
    </Grid>
    <Grid item xs={12} md={7}>
      {input}
    </Grid>
    {rightAction && <Grid item xs={12} md={2}>{rightAction}</Grid>}
  </Grid>
);

// Subcomponent for file upload row
const FileUploadRow = ({ label, file, fileName, onUpload, onDownload }) => (
  <Grid container spacing={2} alignItems="center" sx={{ py: 1 }}>
    <Grid item xs={12} md={3}>
      <Typography variant="body2">{label}</Typography>
    </Grid>
    <Grid item xs={1}>
      <Tooltip title={`Upload ${label}`}>
        <IconButton color="secondary" component="label">
          <CloudUploadIcon />
          <input type="file" hidden accept="application/pdf,image/*" onChange={onUpload} />
        </IconButton>
      </Tooltip>
    </Grid>
    <Grid item xs={1}>
      {file && (
        <Tooltip title={`Download ${label}`}>
          <IconButton color="secondary" onClick={onDownload}>
            <DownloadIcon />
          </IconButton>
        </Tooltip>
      )}
    </Grid>
    <Grid item xs={3}>
      {file && <Typography variant="body2">{fileName}</Typography>}
    </Grid>
  </Grid>
);

export default BankDetailsForm;
